import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Extension, InputRule } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Placeholder from '@tiptap/extension-placeholder';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import { TextAlign } from '@tiptap/extension-text-align';
import Spreadsheet from './SpreadsheetNode';
import { format } from 'date-fns';
import { notesApi } from '@/lib/notesApi';
import NoteAttachments from './NoteAttachments';
import NoteFileViewer from './NoteFileViewer';
import { DictationHint } from './DictationHint';
import { tokenizeDictation, applyOpsToEditor } from './dictationCommands';
import {
  Bold, Italic, Underline as UnderlineIcon, Heading1, Heading2, List, ListOrdered,
  ListChecks, Link2, Palette, X, Trash2, Star, Clock, Share2, Check, Hash, Plus,
  Users2, Lock, Eye, Pencil, Table as TableIcon, Grid3x3, ChevronDown,
  AlignLeft, AlignCenter, AlignRight, Rows3, Columns3, History, Repeat, RotateCcw, Type, Bookmark,
  Quote, Minus, GitCompare, Mic, MicOff,
} from 'lucide-react';

// Table cells extended with an optional fill colour ("format cells like Excel").
const CellBg = {
  addAttributes() {
    return {
      ...this.parent?.(),
      backgroundColor: {
        default: null,
        parseHTML: (el) => el.style.backgroundColor || el.getAttribute('data-bg') || null,
        renderHTML: (attrs) => (attrs.backgroundColor
          ? { style: `background-color:${attrs.backgroundColor}`, 'data-bg': attrs.backgroundColor }
          : {}),
      },
    };
  },
};
const CustomTableCell = TableCell.extend(CellBg);
const CustomTableHeader = TableHeader.extend(CellBg);
const CELL_FILLS = [null, '#FBEFD3', '#E3F0E5', '#E1EAF6', '#F5E1EC', '#EDE6F5'];

// Slash command palette items. Each `run` takes a TipTap chain (already
// focused with the "/query" text removed) and inserts the block.
const SLASH_ITEMS = [
  { key: 'heading1', label: 'Heading 1', hint: 'Large section title', kw: 'h1 title header', Icon: Heading1, run: (c) => c.toggleHeading({ level: 1 }).run() },
  { key: 'heading2', label: 'Heading 2', hint: 'Medium section title', kw: 'h2 subtitle header', Icon: Heading2, run: (c) => c.toggleHeading({ level: 2 }).run() },
  { key: 'checklist', label: 'Checklist', hint: 'To-do list with checkboxes', kw: 'todo task check', Icon: ListChecks, run: (c) => c.toggleTaskList().run() },
  { key: 'bullet', label: 'Bulleted list', hint: 'Simple bullet list', kw: 'ul unordered list', Icon: List, run: (c) => c.toggleBulletList().run() },
  { key: 'numbered', label: 'Numbered list', hint: 'Ordered list', kw: 'ol ordered number list', Icon: ListOrdered, run: (c) => c.toggleOrderedList().run() },
  { key: 'bold', label: 'Bold text', hint: 'Type the next words in bold', kw: 'bold strong emphasis', Icon: Bold, run: (c) => c.toggleBold().run() },
  { key: 'quote', label: 'Quote', hint: 'Block quotation', kw: 'blockquote citation quote', Icon: Quote, run: (c) => c.toggleBlockquote().run() },
  { key: 'divider', label: 'Divider', hint: 'Horizontal line', kw: 'hr horizontal rule line separator divider', Icon: Minus, run: (c) => c.setHorizontalRule().run() },
  { key: 'table', label: 'Table', hint: 'Basic 3×3 table', kw: 'grid table', Icon: TableIcon, run: (c) => c.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
  { key: 'spreadsheet', label: 'Spreadsheet', hint: 'Mini-spreadsheet with formulas', kw: 'sheet excel formula', Icon: Grid3x3, run: (c) => c.insertSpreadsheet(4, 4).run() },
];

// ISO string -> value for <input type="datetime-local"> (local timezone).
function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const COLORS = [
  { name: 'Default', value: null },
  { name: 'Gold', value: '#B8892E' },
  { name: 'Ink', value: '#1F2328' },
  { name: 'Rose', value: '#B42318' },
  { name: 'Green', value: '#1F7A5A' },
  { name: 'Blue', value: '#1E4E8C' },
  { name: 'Plum', value: '#8A2D55' },
];

const TB = ({ active, onClick, title, children, testid, disabled }) => (
  <button
    type="button" title={title} aria-label={title} onClick={onClick} disabled={disabled}
    data-testid={testid}
    className={`h-9 w-9 inline-flex items-center justify-center rounded-lg text-[var(--n-ink-soft)] transition-colors
      hover:bg-[var(--n-canvas-2)] disabled:opacity-40
      ${active ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)] ring-1 ring-[var(--n-accent-soft)]' : ''}`}
  >
    {children}
  </button>
);
const Sep = () => <span className="mx-1 h-5 w-px bg-[var(--n-border)]" />;

// Insert + edit tables and spreadsheets. Insert lets the writer choose a
// Basic table or a formula-capable mini-spreadsheet; edit controls apply to
// the table under the cursor.
function TableMenu({ editor }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  if (!editor) return null;
  const inTable = editor.isActive('table');
  const clamp = (n) => Math.max(1, Math.min(30, parseInt(n, 10) || 1));
  const insertTable = () => { editor.chain().focus().insertTable({ rows: clamp(rows), cols: clamp(cols), withHeaderRow: true }).run(); setOpen(false); };
  const insertSheet = () => { editor.chain().focus().insertSpreadsheet(clamp(rows), clamp(cols)).run(); setOpen(false); };
  const cmd = (fn) => () => { fn(editor.chain().focus()).run(); };
  const smallBtn = 'inline-flex items-center justify-center gap-1 rounded-md border border-[var(--n-border)] px-2 py-1.5 text-[12px] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] disabled:opacity-40';
  const numCls = 'w-12 rounded-md border border-[var(--n-border)] bg-[var(--n-surface)] px-1.5 py-1 text-[12px] text-[var(--n-ink)]';
  return (
    <div className="relative" ref={ref}>
      <button type="button" title="Insert table or mini-spreadsheet" data-testid="note-table-menu-button"
        onClick={() => setOpen((o) => !o)}
        className={`h-9 inline-flex items-center gap-1 rounded-lg px-2.5 text-[var(--n-ink-soft)] transition-colors hover:bg-[var(--n-canvas-2)] ${open || inTable ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)] ring-1 ring-[var(--n-accent-soft)]' : ''}`}>
        <TableIcon size={16} /> <span className="hidden md:inline text-[13px] font-medium">Table</span> <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute left-0 z-50 mt-1 w-64 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-2.5 shadow-[var(--n-shadow)]" data-testid="note-table-menu">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Insert</div>
          <div className="mb-2 flex items-center gap-2">
            <label className="text-[11px] text-[var(--n-muted)]">Rows</label>
            <input type="number" min={1} max={30} value={rows} onChange={(e) => setRows(e.target.value)} className={numCls} data-testid="note-table-rows-input" />
            <label className="text-[11px] text-[var(--n-muted)]">Cols</label>
            <input type="number" min={1} max={30} value={cols} onChange={(e) => setCols(e.target.value)} className={numCls} data-testid="note-table-cols-input" />
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <button type="button" onClick={insertTable} className={smallBtn} data-testid="note-insert-table"><TableIcon size={13} /> Basic table</button>
            <button type="button" onClick={insertSheet} className={smallBtn} data-testid="note-insert-spreadsheet"><Grid3x3 size={13} /> Spreadsheet</button>
          </div>
          <p className="mt-1.5 text-[10px] leading-snug text-[var(--n-muted-2)]">Formulas (=A1+B1) supported. Paste an Excel/Sheets range to drop a live spreadsheet, or type <code>/table</code> or <code>/sheet</code> in the note.</p>
          <div className="my-2.5 h-px bg-[var(--n-line)]" />
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Edit table</div>
          <div className="grid grid-cols-3 gap-1.5">
            <button type="button" disabled={!inTable} onClick={cmd((c) => c.addRowAfter())} className={smallBtn} data-testid="note-table-add-row"><Rows3 size={12} /> +Row</button>
            <button type="button" disabled={!inTable} onClick={cmd((c) => c.addColumnAfter())} className={smallBtn} data-testid="note-table-add-col"><Columns3 size={12} /> +Col</button>
            <button type="button" disabled={!inTable} onClick={cmd((c) => c.toggleHeaderRow())} className={smallBtn}>Header</button>
            <button type="button" disabled={!inTable} onClick={cmd((c) => c.deleteRow())} className={smallBtn}>−Row</button>
            <button type="button" disabled={!inTable} onClick={cmd((c) => c.deleteColumn())} className={smallBtn}>−Col</button>
            <button type="button" disabled={!inTable} onClick={cmd((c) => c.deleteTable())} className={`${smallBtn} text-[var(--n-danger)]`} data-testid="note-table-delete">Delete</button>
          </div>
          <div className="mt-2 flex items-center gap-1.5">
            <span className="text-[11px] text-[var(--n-muted)]">Align</span>
            <button type="button" disabled={!inTable} onClick={cmd((c) => c.setTextAlign('left'))} className={smallBtn}><AlignLeft size={13} /></button>
            <button type="button" disabled={!inTable} onClick={cmd((c) => c.setTextAlign('center'))} className={smallBtn}><AlignCenter size={13} /></button>
            <button type="button" disabled={!inTable} onClick={cmd((c) => c.setTextAlign('right'))} className={smallBtn}><AlignRight size={13} /></button>
          </div>
          <div className="mt-2 flex items-center gap-1.5">
            <span className="text-[11px] text-[var(--n-muted)]">Fill</span>
            {CELL_FILLS.map((color, i) => (
              <button key={i} type="button" disabled={!inTable} title={color || 'No fill'} onClick={cmd((c) => c.setCellAttribute('backgroundColor', color))}
                className="h-5 w-5 rounded ring-1 ring-inset ring-[var(--n-border)] disabled:opacity-40" style={{ background: color || 'var(--n-surface)' }}>
                {!color && <X size={10} className="mx-auto text-[var(--n-muted-2)]" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function NoteEditor({ note, folders = [], allTags = [], onSave, onDelete, onToggleFavorite, onAttachmentsChanged, onOpenShare, onRestored, presence = [], currentUserId }) {
  // Access derived from the note payload (owned notes have no role → owner).
  const isOwner = !note.role || note.role === 'owner';
  const canEdit = note.permission ? note.permission === 'edit' : true;
  const readOnly = !canEdit;
  const sharedBy = note.shared_by;
  // Live presence — everyone else currently viewing/editing this note.
  const others = (presence || []).filter((u) => u && u.id && u.id !== currentUserId);

  const [title, setTitle] = useState(note.title === 'Untitled' ? '' : (note.title || ''));
  const [tags, setTags] = useState(note.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [tagFocused, setTagFocused] = useState(false);
  const [tagActiveIdx, setTagActiveIdx] = useState(-1);
  const [showColors, setShowColors] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [reminderDraft, setReminderDraft] = useState(() => isoToLocalInput(note.reminder_at));
  const [repeatDraft, setRepeatDraft] = useState(note.reminder_repeat || 'none');
  const reminderRef = useRef(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [viewerAtt, setViewerAtt] = useState(null); // split-screen file viewer
  const [historyOpen, setHistoryOpen] = useState(false); // version history dialog
  // Voice dictation (Web Speech API) — free, browser-native, English.
  const [listening, setListening] = useState(false);
  const [interimText, setInterimText] = useState('');
  const speechSupported = typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  const recognitionRef = useRef(null);
  const shouldListenRef = useRef(false);
  const saveTimer = useRef(null);
  const latest = useRef({});

  // ---- Slash command palette (type "/" for a block picker) ----------------
  const [slash, setSlash] = useState(null); // { items, index, from, to, top, left }
  const slashRef = useRef(null);
  const slashApiRef = useRef({ isOpen: () => false, move: () => {}, apply: () => {}, close: () => {} });
  const detectSlashRef = useRef(null);
  const slashBoxRef = useRef(null);
  useEffect(() => { slashRef.current = slash; }, [slash]);

  const SlashMenuExt = useMemo(() => Extension.create({
    name: 'slashMenu',
    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey('slashMenuKeys'),
          props: {
            handleKeyDown(view, event) {
              const api = slashApiRef.current;
              if (!api.isOpen()) return false;
              if (event.key === 'ArrowDown') { event.preventDefault(); api.move(1); return true; }
              if (event.key === 'ArrowUp') { event.preventDefault(); api.move(-1); return true; }
              if (event.key === 'Enter' || event.key === 'Tab') { event.preventDefault(); api.apply(); return true; }
              if (event.key === 'Escape') { event.preventDefault(); api.close(); return true; }
              return false;
            },
          },
        }),
      ];
    },
  }), []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      TextStyle,
      Color,
      TaskList,
      TaskItem.configure({ nested: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Table.configure({ resizable: true }),
      TableRow,
      CustomTableHeader,
      CustomTableCell,
      Spreadsheet,
      SlashMenuExt,
      Placeholder.configure({ placeholder: "Start writing… type '/' for headings, lists, tables and more." }),
    ],
    content: note.body_html || '',
    editable: !readOnly,
    editorProps: { attributes: { class: 'min-h-[50vh] focus:outline-none' } },
    onUpdate: ({ editor }) => { if (!readOnly) queueSave({ body_html: editor.getHTML() }); if (detectSlashRef.current) detectSlashRef.current(editor); },
    onSelectionUpdate: ({ editor }) => { if (detectSlashRef.current) detectSlashRef.current(editor); },
    onBlur: () => { setTimeout(() => { if (slashRef.current) setSlash(null); }, 150); },
  });

  // ---- Voice dictation ------------------------------------------------------
  // Parses finalised speech into text + spoken commands (punctuation, breaks,
  // quotes, "delete that") and applies them at the cursor with smart spacing.
  const insertDictated = useCallback((raw) => {
    if (!editor) return;
    const ops = tokenizeDictation(raw);
    if (!ops.length) return;
    applyOpsToEditor(editor, ops);
  }, [editor]);

  const stopDictation = useCallback(() => {
    shouldListenRef.current = false;
    setListening(false);
    setInterimText('');
    try { recognitionRef.current && recognitionRef.current.stop(); } catch { /* noop */ }
  }, []);

  const startDictation = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR || !editor || readOnly) return;
    let rec = recognitionRef.current;
    if (!rec) {
      rec = new SR();
      rec.lang = 'en-US';
      rec.continuous = true;
      rec.interimResults = true;
      rec.onresult = (e) => {
        let finalText = '';
        let interim = '';
        for (let i = e.resultIndex; i < e.results.length; i += 1) {
          const seg = e.results[i];
          if (seg.isFinal) finalText += seg[0].transcript;
          else interim += seg[0].transcript;
        }
        if (finalText) insertDictated(finalText);
        setInterimText(interim);
      };
      rec.onerror = (e) => {
        if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
          shouldListenRef.current = false;
          setListening(false);
          setInterimText('');
        }
      };
      rec.onend = () => {
        setInterimText('');
        // Chrome ends the session after a pause; auto-restart until the user stops.
        if (shouldListenRef.current) { try { rec.start(); } catch { /* noop */ } }
        else setListening(false);
      };
      recognitionRef.current = rec;
    }
    shouldListenRef.current = true;
    try { rec.start(); setListening(true); } catch { /* already started */ }
  }, [editor, readOnly, insertDictated]);

  const toggleDictation = useCallback(() => {
    if (listening) stopDictation(); else startDictation();
  }, [listening, startDictation, stopDictation]);

  // Stop listening if the note changes or the editor unmounts.
  useEffect(() => () => { shouldListenRef.current = false; try { recognitionRef.current && recognitionRef.current.abort(); } catch { /* noop */ } }, []);
  useEffect(() => { if (listening) stopDictation(); /* eslint-disable-next-line */ }, [note.id]);

  const flushSave = useCallback(async () => {
    if (readOnly) { latest.current = {}; return; }
    const patch = latest.current;
    latest.current = {};
    if (!patch || Object.keys(patch).length === 0) return;
    await onSave(patch);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1200);
  }, [onSave, readOnly]);

  const queueSave = useCallback((patch) => {
    if (readOnly) return;
    latest.current = { ...latest.current, ...patch };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushSave, 650);
  }, [flushSave, readOnly]);

  // Flush pending save on unmount / note switch.
  useEffect(() => () => { if (saveTimer.current) { clearTimeout(saveTimer.current); flushSave(); } }, [flushSave]);

  // Keep editability in sync if the permission changes (e.g. live share update).
  useEffect(() => { if (editor) editor.setEditable(!readOnly); }, [editor, readOnly]);

  // Close the reminder popover on outside click.
  useEffect(() => {
    const h = (e) => { if (reminderRef.current && !reminderRef.current.contains(e.target)) setShowReminder(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ---- Slash palette: detect "/query", filter items, position + keyboard ----
  const detectSlash = useCallback((ed) => {
    const e = ed || editor;
    if (!e || e.isEditable === false) { setSlash(null); return; }
    const { $from, empty } = e.state.selection;
    if (!empty || !$from.parent.isTextblock) { setSlash(null); return; }
    const textBefore = $from.parent.textBetween(0, $from.parentOffset, '\n', '\ufffc');
    const m = /(?:^|\s)\/([a-zA-Z0-9]*)$/.exec(textBefore);
    if (!m) { setSlash(null); return; }
    const query = m[1].toLowerCase();
    const items = SLASH_ITEMS.filter((it) => !query || it.label.toLowerCase().includes(query) || it.kw.includes(query) || it.key.includes(query));
    if (!items.length) { setSlash(null); return; }
    const to = $from.pos;
    const from = to - (query.length + 1);
    let coords;
    try { coords = e.view.coordsAtPos(to); } catch { coords = null; }
    if (!coords) { setSlash(null); return; }
    setSlash((prev) => ({
      items, from, to,
      index: prev && prev.from === from && prev.index < items.length ? prev.index : 0,
      top: coords.bottom + 6, left: coords.left,
    }));
  }, [editor]);
  useEffect(() => { detectSlashRef.current = detectSlash; }, [detectSlash]);

  const applySlashItem = useCallback((item) => {
    const cur = slashRef.current;
    if (!cur || !editor) { setSlash(null); return; }
    const it = item || cur.items[cur.index];
    setSlash(null);
    if (!it) return;
    it.run(editor.chain().focus().deleteRange({ from: cur.from, to: cur.to }));
  }, [editor]);

  useEffect(() => {
    slashApiRef.current = {
      isOpen: () => !!slashRef.current,
      move: (dir) => setSlash((s) => (s ? { ...s, index: (s.index + dir + s.items.length) % s.items.length } : s)),
      apply: () => applySlashItem(),
      close: () => setSlash(null),
    };
  }, [applySlashItem]);

  // Close the slash palette on outside click.
  useEffect(() => {
    const h = (e) => { if (slashBoxRef.current && !slashBoxRef.current.contains(e.target)) setSlash(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const commitTitle = (v) => { setTitle(v); queueSave({ title: v || 'Untitled' }); };
  const addTag = (raw) => {
    const t = String(raw).trim().replace(/^#/, '');
    if (!t) return;
    if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) { setTagInput(''); return; }
    const next = [...tags, t];
    setTags(next); setTagInput('');
    queueSave({ tags: next });
  };
  const removeTag = (t) => { const next = tags.filter((x) => x !== t); setTags(next); queueSave({ tags: next }); };

  if (!editor) return null;
  const reminderActive = !!note.reminder_at;

  // Tag autocomplete — match existing tags (excluding already-added) and
  // offer a "create new" option when the typed value is not an exact match.
  const tq = tagInput.trim().replace(/^#/, '').toLowerCase();
  const tagSuggestions = tq
    ? allTags.filter((t) => t.toLowerCase().includes(tq) && !tags.some((x) => x.toLowerCase() === t.toLowerCase())).slice(0, 6)
    : [];
  const exactMatch = allTags.some((t) => t.toLowerCase() === tq) || tags.some((t) => t.toLowerCase() === tq);
  const showCreate = !!tq && !exactMatch;
  const tagItemCount = tagSuggestions.length + (showCreate ? 1 : 0);
  const pickTagItem = (idx) => {
    if (idx >= 0 && idx < tagSuggestions.length) addTag(tagSuggestions[idx]);
    else addTag(tq);
    setTagActiveIdx(-1);
  };

  // Reminder derived state + handlers.
  const reminderLabel = note.reminder_at ? format(new Date(note.reminder_at), 'MMM d, h:mm a') : '';
  const reminderOverdue = note.reminder_at && new Date(note.reminder_at).getTime() <= Date.now();
  const applyReminder = () => {
    if (!reminderDraft) { onSave?.({ reminder_at: '', reminder_repeat: 'none' }); }
    else { onSave?.({ reminder_at: new Date(reminderDraft).toISOString(), reminder_repeat: repeatDraft || 'none' }); }
    setShowReminder(false);
  };
  const clearReminder = () => { setReminderDraft(''); setRepeatDraft('none'); onSave?.({ reminder_at: '', reminder_repeat: 'none' }); setShowReminder(false); };

  // Share via device share sheet, with a mailto fallback.
  const handleShare = async () => {
    const text = (editor?.getText?.() || '').trim();
    const body = `${note.title ? note.title + '\n\n' : ''}${text}`.slice(0, 5000);
    try {
      if (navigator.share) await navigator.share({ title: note.title || 'Note', text: body });
      else window.location.href = `mailto:?subject=${encodeURIComponent(note.title || 'Note')}&body=${encodeURIComponent(body)}`;
    } catch { /* user cancelled / unsupported */ }
  };

  return (
    <div className="flex h-full flex-col" data-testid="note-editor">
      {!isOwner && (
        <div className="flex items-center gap-2 border-b border-[var(--n-line)] bg-[var(--n-accent-bg)] px-4 py-2 text-[12px] text-[var(--n-accent)]" data-testid="note-shared-banner">
          {readOnly ? <Eye size={13} /> : <Pencil size={13} />}
          <span className="font-medium">{sharedBy ? `Shared by ${sharedBy}` : 'Shared with you'}</span>
          <span className="opacity-70">· {readOnly ? 'View only' : 'You can edit'}</span>
        </div>
      )}
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 border-b border-[var(--n-line)] px-3 py-2 flex-wrap" data-testid="note-editor-toolbar">
        {!readOnly && (<>
        <TB title="Heading 1" active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><Heading1 size={17} /></TB>
        <TB title="Heading 2" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={17} /></TB>
        <Sep />
        <TB title="Bold" testid="note-editor-bold-button" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={16} /></TB>
        <TB title="Italic" testid="note-editor-italic-button" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={16} /></TB>
        <TB title="Underline" testid="note-editor-underline-button" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon size={16} /></TB>
        <div className="relative">
          <TB title="Font colour" testid="note-editor-color-button" active={showColors || editor.isActive('textStyle')} onClick={() => setShowColors((s) => !s)}><Palette size={16} /></TB>
          {showColors && (
            <div className="absolute z-50 mt-1 left-0 flex gap-1.5 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-2 shadow-[var(--n-shadow)]" data-testid="note-editor-color-swatches">
              {COLORS.map((c) => (
                <button key={c.name} title={c.name}
                  onClick={() => { if (c.value) editor.chain().focus().setColor(c.value).run(); else editor.chain().focus().unsetColor().run(); setShowColors(false); }}
                  className="h-6 w-6 rounded-md ring-1 ring-inset ring-[var(--n-border)] flex items-center justify-center"
                  style={{ background: c.value || 'var(--n-surface)' }}>
                  {!c.value && <X size={12} className="text-[var(--n-muted)]" />}
                </button>
              ))}
            </div>
          )}
        </div>
        <Sep />
        <TB title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={17} /></TB>
        <TB title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={17} /></TB>
        <TB title="Checklist" testid="note-editor-tasklist-button" active={editor.isActive('taskList')} onClick={() => editor.chain().focus().toggleTaskList().run()}><ListChecks size={17} /></TB>
        <TB title="Link" active={editor.isActive('link')} onClick={() => {
          const prev = editor.getAttributes('link')?.href || '';
          const url = window.prompt('Link URL', prev);
          if (url === null) return;
          if (url === '') editor.chain().focus().extendMarkRange('link').unsetLink().run();
          else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        }}><Link2 size={16} /></TB>
        <Sep />
        <TableMenu editor={editor} />
        <Sep />
        {speechSupported ? (
          <TB title={listening ? 'Stop dictation' : 'Dictate (voice to text)'}
            testid="note-editor-dictate-button" active={listening} onClick={toggleDictation}>
            {listening ? <MicOff size={16} /> : <Mic size={16} />}
          </TB>
        ) : (
          <TB title="Voice dictation isn't supported in this browser (try Chrome, Edge or Safari)"
            testid="note-editor-dictate-unsupported" disabled onClick={() => {}}>
            <Mic size={16} />
          </TB>
        )}
        {speechSupported && (
          <DictationHint triggerClassName="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)] transition-colors n-focus" />
        )}
        {listening && (
          <span className="ml-1 inline-flex max-w-[240px] items-center gap-1.5 rounded-full bg-[var(--n-danger,#e5484d)]/10 px-2 py-0.5 text-[11px] font-medium text-[var(--n-danger,#e5484d)]" data-testid="note-editor-dictate-status">
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-current" />
            </span>
            <span className="truncate">{interimText ? interimText : 'Listening…'}</span>
          </span>
        )}
        </>)}

        <div className="ml-auto flex items-center gap-1.5">
          {others.length > 0 && (
            <div className="mr-1 flex items-center -space-x-1.5" data-testid="note-presence" title={`Here now: ${others.map((u) => u.name).join(', ')}`}>
              {others.slice(0, 3).map((u) => (
                <span key={u.id} title={u.name}
                  className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-[var(--n-surface)] bg-[var(--n-accent)] text-[9px] font-bold text-white">
                  {(u.name || '?').slice(0, 1).toUpperCase()}
                </span>
              ))}
              {others.length > 3 && (
                <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-[var(--n-surface)] bg-[var(--n-canvas-2)] text-[9px] font-bold text-[var(--n-ink-soft)]">+{others.length - 3}</span>
              )}
            </div>
          )}
          {!readOnly && (
            <span className={`text-[11px] transition-opacity ${savedFlash ? 'opacity-100 text-[var(--n-success)]' : 'opacity-0'}`} data-testid="note-saved-flash">
              <Check size={12} className="inline -mt-0.5" /> Saved
            </span>
          )}
          {isOwner && (
          <div className="relative" ref={reminderRef}>
            <button onClick={() => { setReminderDraft(isoToLocalInput(note.reminder_at)); setShowReminder((s) => !s); }} title="Set reminder"
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-[11px] font-semibold transition-colors
                ${reminderActive
                  ? (reminderOverdue ? 'border-[var(--n-danger)] bg-[var(--n-danger)]/10 text-[var(--n-danger)]' : 'border-[var(--n-accent-soft)] bg-[var(--n-accent-bg)] text-[var(--n-accent)]')
                  : 'border-[var(--n-border)] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}
              data-testid="note-reminder-set-button">
              <Clock size={13} /> {reminderActive ? reminderLabel : 'Reminder'}
            </button>
            {showReminder && (
              <div className="absolute right-0 z-50 mt-1 w-64 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-3 shadow-[var(--n-shadow)]" data-testid="note-reminder-popover">
                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Remind me at</div>
                <input type="datetime-local" value={reminderDraft} onChange={(e) => setReminderDraft(e.target.value)}
                  className="n-focus w-full rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas)] px-2.5 py-2 text-[13px] text-[var(--n-ink)]" data-testid="note-reminder-input" />
                <div className="mt-2.5 mb-0.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]"><Repeat size={11} /> Repeat</div>
                <div className="grid grid-cols-3 gap-1" data-testid="note-reminder-repeat">
                  {[['none', 'Once'], ['daily', 'Daily'], ['weekly', 'Weekly']].map(([val, lbl]) => (
                    <button key={val} type="button" onClick={() => setRepeatDraft(val)} data-testid={`note-reminder-repeat-${val}`}
                      className={`n-focus rounded-lg border px-2 py-1.5 text-[11px] font-medium transition-colors ${repeatDraft === val ? 'border-[var(--n-accent-soft)] bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'border-[var(--n-border)] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <button onClick={clearReminder} disabled={!note.reminder_at} data-testid="note-reminder-clear"
                    className="text-[12px] font-medium text-[var(--n-muted)] hover:text-[var(--n-danger)] disabled:opacity-40">Clear</button>
                  <button onClick={applyReminder} disabled={!reminderDraft} data-testid="note-reminder-save"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--n-accent)] px-3 py-1.5 text-[12px] font-semibold text-white hover:opacity-90 disabled:opacity-40">Set reminder</button>
                </div>
              </div>
            )}
          </div>
          )}
          {isOwner && (
            <button onClick={() => onOpenShare?.({ type: 'note', id: note.id, name: note.title || 'Untitled' })} title="Share with people"
              className="inline-flex items-center gap-1.5 rounded-full border border-[var(--n-accent-soft)] bg-[var(--n-accent-bg)] px-2.5 py-1.5 text-[11px] font-semibold text-[var(--n-accent)] hover:opacity-90"
              data-testid="note-share-people-button">
              <Users2 size={13} /> Share
            </button>
          )}
          <button onClick={handleShare} title="Share as text (device / email)"
            className="inline-flex items-center gap-1.5 rounded-full border border-[var(--n-border)] px-2.5 py-1.5 text-[11px] font-semibold text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]"
            data-testid="note-share-button">
            <Share2 size={13} /> Export
          </button>
          {isOwner && (
            <TB title={note.is_favorite ? 'Unfavourite' : 'Favourite'} active={note.is_favorite} onClick={() => onToggleFavorite?.(note)}>
              <Star size={16} className={note.is_favorite ? 'fill-[var(--n-accent)] text-[var(--n-accent)]' : ''} />
            </TB>
          )}
          {isOwner && (
            <TB title="Version history" testid="note-history-button" onClick={() => setHistoryOpen(true)}><History size={16} /></TB>
          )}
          {isOwner && (
            <TB title="Delete note" testid="note-delete-button" onClick={() => onDelete?.(note)}><Trash2 size={16} /></TB>
          )}
        </div>
      </div>

      {/* Canvas (+ optional split-screen file viewer) */}
      <div className="flex min-h-0 flex-1">
        <div className={`overflow-y-auto n-scroll ${viewerAtt ? 'w-1/2 border-r border-[var(--n-line)]' : 'flex-1'}`} data-testid="note-canvas-pane">
          <div className={`mx-auto w-full py-8 ${viewerAtt ? 'max-w-none px-6' : 'max-w-[760px] px-6 md:px-8'}`}>
          <input
            value={title} onChange={(e) => commitTitle(e.target.value)} placeholder="Untitled" readOnly={readOnly}
            className="font-display w-full bg-transparent text-3xl md:text-4xl font-bold tracking-[-0.02em] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)] focus:outline-none"
            data-testid="note-editor-title-input"
          />
          {/* Tags */}
          <div className="mt-3 mb-5 flex flex-wrap items-center gap-1.5" data-testid="note-tags-editor">
            {tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[var(--n-accent-bg)] px-2.5 py-1 text-[12px] font-medium text-[var(--n-accent)]">
                #{t}
                {!readOnly && <button onClick={() => removeTag(t)} className="opacity-70 hover:opacity-100" aria-label={`Remove ${t}`}><X size={11} /></button>}
              </span>
            ))}
            {!readOnly && (
            <div className="relative min-w-[120px] flex-1">
              <input
                value={tagInput}
                onChange={(e) => { setTagInput(e.target.value); setTagActiveIdx(-1); setTagFocused(true); }}
                onFocus={() => setTagFocused(true)}
                onBlur={() => setTimeout(() => setTagFocused(false), 120)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') { if (tagItemCount) { e.preventDefault(); setTagActiveIdx((i) => (i + 1) % tagItemCount); } }
                  else if (e.key === 'ArrowUp') { if (tagItemCount) { e.preventDefault(); setTagActiveIdx((i) => (i <= 0 ? tagItemCount - 1 : i - 1)); } }
                  else if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    if (tagActiveIdx >= 0 && tagActiveIdx < tagItemCount) pickTagItem(tagActiveIdx);
                    else addTag(tagInput);
                  }
                  else if (e.key === 'Escape') { setTagFocused(false); setTagActiveIdx(-1); }
                  else if (e.key === 'Backspace' && !tagInput && tags.length) removeTag(tags[tags.length - 1]);
                }}
                placeholder={tags.length ? 'Add tag…' : 'Add tags…'}
                className="w-full bg-transparent text-[12px] text-[var(--n-ink-soft)] placeholder:text-[var(--n-muted-2)] focus:outline-none"
                data-testid="note-tag-input"
                autoComplete="off"
              />
              {tagFocused && (tagSuggestions.length > 0 || showCreate) && (
                <div className="absolute left-0 top-full z-50 mt-1 w-56 max-h-64 overflow-y-auto n-scroll rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]" data-testid="note-tag-suggestions">
                  {tagSuggestions.map((t, i) => (
                    <button key={t} onMouseDown={(e) => { e.preventDefault(); addTag(t); }} data-testid="note-tag-suggestion"
                      className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] ${i === tagActiveIdx ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                      <Hash size={12} className="shrink-0 text-[var(--n-muted-2)]" /> <span className="truncate">{t}</span>
                    </button>
                  ))}
                  {showCreate && (
                    <button onMouseDown={(e) => { e.preventDefault(); addTag(tq); }} data-testid="note-tag-create-option"
                      className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] ${tagActiveIdx === tagSuggestions.length ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                      <Plus size={12} className="shrink-0" /> Create “#{tq}”
                    </button>
                  )}
                </div>
              )}
            </div>
            )}
          </div>
          <EditorContent editor={editor} />
          <NoteAttachments
            noteId={note.id}
            initial={note.attachments || []}
            onChange={onAttachmentsChanged}
            readOnly={readOnly}
            onOpenViewer={setViewerAtt}
          />
          </div>
        </div>
        {viewerAtt && (
          <div className="hidden md:block w-1/2 min-w-0" data-testid="note-viewer-pane">
            <NoteFileViewer noteId={note.id} att={viewerAtt} onClose={() => setViewerAtt(null)} />
          </div>
        )}
      </div>

      {/* Slash command palette */}
      {slash && (
        <div ref={slashBoxRef} data-testid="note-slash-menu"
          className="fixed z-[240] w-64 overflow-hidden rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]"
          style={{ top: slash.top, left: slash.left }}>
          <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Insert block</div>
          {slash.items.map((it, i) => (
            <button key={it.key} type="button"
              onMouseDown={(e) => { e.preventDefault(); applySlashItem(it); }}
              onMouseEnter={() => setSlash((s) => (s ? { ...s, index: i } : s))}
              data-testid={`note-slash-item-${it.key}`}
              className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left ${i === slash.index ? 'bg-[var(--n-accent-bg)]' : 'hover:bg-[var(--n-canvas-2)]'}`}>
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${i === slash.index ? 'bg-[var(--n-accent)] text-white' : 'bg-[var(--n-canvas-2)] text-[var(--n-ink-soft)]'}`}>
                <it.Icon size={14} />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-[13px] font-medium text-[var(--n-ink)]">{it.label}</span>
                <span className="block truncate text-[11px] text-[var(--n-muted)]">{it.hint}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Version history */}
      {historyOpen && (
        <VersionHistory noteId={note.id} onClose={() => setHistoryOpen(false)}
          getCurrentHtml={() => (editor ? editor.getHTML() : note.body_html)}
          onRestored={(updated) => { setHistoryOpen(false); onRestored?.(updated); }} />
      )}
    </div>
  );
}

function formatVersionDate(iso) {
  if (!iso) return '';
  try { return format(new Date(iso), 'MMM d, yyyy · h:mm a'); } catch { return ''; }
}

// Strip HTML to readable plain text (block tags → line breaks) for diffing.
function htmlToPlain(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll('p,div,li,h1,h2,h3,h4,br,tr,blockquote,hr').forEach((el) => { el.appendChild(document.createTextNode('\n')); });
  return (div.textContent || '').replace(/\n{3,}/g, '\n\n').replace(/[ \t]+\n/g, '\n').trim();
}

// Word-level diff via LCS → segments tagged same / add / del.
function wordDiff(oldText, newText) {
  const a = (oldText || '').split(/(\s+)/);
  const b = (newText || '').split(/(\s+)/);
  const n = a.length, m = b.length;
  // Guard against pathological sizes (keep memory sane for very long notes).
  if (n > 4000 || m > 4000) {
    return [{ type: oldText === newText ? 'same' : 'add', text: newText }];
  }
  const dp = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out = [];
  const push = (type, text) => {
    const last = out[out.length - 1];
    if (last && last.type === type) last.text += text;
    else out.push({ type, text });
  };
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { push('same', a[i]); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { push('del', a[i]); i++; }
    else { push('add', b[j]); j++; }
  }
  while (i < n) { push('del', a[i]); i++; }
  while (j < m) { push('add', b[j]); j++; }
  return out;
}

// Version history dialog — list snapshots, name/rename, preview, diff, and restore.
function VersionHistory({ noteId, onClose, onRestored, getCurrentHtml }) {
  const [versions, setVersions] = useState(null); // null = loading
  const [selId, setSelId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [err, setErr] = useState('');
  const [saveMode, setSaveMode] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState('');
  const [mode, setMode] = useState('preview'); // 'preview' | 'diff'
  const currentText = useMemo(() => htmlToPlain(getCurrentHtml ? getCurrentHtml() : ''), [getCurrentHtml]);
  const diff = useMemo(
    () => (selected ? wordDiff(htmlToPlain(selected.body_html), currentText) : []),
    [selected, currentText],
  );

  const reload = useCallback(async (preferId) => {
    try {
      const { data } = await notesApi.get(`/notes/notes/${noteId}/versions`);
      setVersions(data || []);
      if (data && data.length) {
        setSelId((cur) => preferId || (data.find((v) => v.id === cur) ? cur : data[0].id));
      } else { setSelId(null); }
    } catch { setVersions([]); setErr('Could not load history.'); }
  }, [noteId]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (!selId) { setSelected(null); return; }
    let stop = false;
    setRenaming(false);
    notesApi.get(`/notes/notes/${noteId}/versions/${selId}`)
      .then(({ data }) => { if (!stop) setSelected(data); })
      .catch(() => { if (!stop) setSelected(null); });
    return () => { stop = true; };
  }, [selId, noteId]);

  const restore = async () => {
    if (!selId) return;
    setRestoring(true); setErr('');
    try {
      const { data } = await notesApi.post(`/notes/notes/${noteId}/versions/${selId}/restore`);
      onRestored?.(data);
    } catch { setErr('Restore failed. Please try again.'); setRestoring(false); }
  };

  const saveNamed = async () => {
    setErr('');
    try {
      const { data } = await notesApi.post(`/notes/notes/${noteId}/versions`, { label: saveName.trim() });
      setSaveMode(false); setSaveName('');
      await reload(data && data.id);
    } catch { setErr('Could not save this version.'); }
  };

  const applyRename = async () => {
    if (!selId) return;
    setErr('');
    try {
      await notesApi.patch(`/notes/notes/${noteId}/versions/${selId}`, { label: renameVal.trim() });
      setRenaming(false);
      setSelected((s) => (s ? { ...s, label: renameVal.trim() } : s));
      await reload(selId);
    } catch { setErr('Could not rename this version.'); }
  };

  return (
    <div className="fixed inset-0 z-[280] flex items-center justify-center bg-black/40 p-4" data-testid="note-history-dialog" onMouseDown={onClose}>
      <div className="flex h-[560px] max-h-[85vh] w-full max-w-3xl overflow-hidden rounded-2xl border border-[var(--n-border)] bg-[var(--n-surface)] shadow-[var(--n-shadow)]" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex w-72 shrink-0 flex-col border-r border-[var(--n-line)]">
          <div className="flex items-center justify-between gap-2 border-b border-[var(--n-line)] px-4 py-3">
            <div className="flex items-center gap-2">
              <History size={16} className="text-[var(--n-accent)]" />
              <span className="text-sm font-semibold text-[var(--n-ink)]">Version history</span>
            </div>
            <button onClick={() => { setSaveMode((s) => !s); setSaveName(''); }} data-testid="note-history-save-current"
              title="Save current as a named version"
              className="n-focus inline-flex items-center gap-1 rounded-lg border border-[var(--n-border)] px-2 py-1 text-[11px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
              <Bookmark size={12} /> Save
            </button>
          </div>
          {saveMode && (
            <div className="flex items-center gap-1.5 border-b border-[var(--n-line)] bg-[var(--n-canvas-2)] px-3 py-2">
              <input autoFocus value={saveName} onChange={(e) => setSaveName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveNamed(); if (e.key === 'Escape') setSaveMode(false); }}
                placeholder="Name (e.g. Final draft)" data-testid="note-history-save-input"
                className="n-focus min-w-0 flex-1 rounded-md border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1 text-[12px] text-[var(--n-ink)]" />
              <button onClick={saveNamed} data-testid="note-history-save-confirm" className="rounded-md bg-[var(--n-accent)] px-2 py-1 text-[11px] font-semibold text-white hover:opacity-90">Save</button>
            </div>
          )}
          <div className="flex-1 overflow-y-auto n-scroll p-2">
            {versions === null ? (
              <div className="space-y-2 p-1">{[0, 1, 2].map((i) => <div key={i} className="h-12 rounded-lg bg-[var(--n-canvas-2)]" />)}</div>
            ) : versions.length === 0 ? (
              <div className="px-3 py-8 text-center text-[12px] text-[var(--n-muted)]" data-testid="note-history-empty">No previous versions yet. Snapshots are saved automatically as you edit — or use “Save” to name the current one.</div>
            ) : (
              versions.map((v) => (
                <button key={v.id} onClick={() => setSelId(v.id)} data-testid="note-history-item"
                  className={`mb-1 flex w-full flex-col gap-0.5 rounded-lg px-3 py-2 text-left ${selId === v.id ? 'bg-[var(--n-accent-bg)]' : 'hover:bg-[var(--n-canvas-2)]'}`}>
                  {v.label ? (
                    <span className={`flex items-center gap-1.5 text-[12px] font-semibold ${selId === v.id ? 'text-[var(--n-accent)]' : 'text-[var(--n-ink)]'}`}>
                      <Bookmark size={12} className="shrink-0" /> <span className="truncate">{v.label}</span>
                    </span>
                  ) : (
                    <span className={`text-[12px] font-semibold ${selId === v.id ? 'text-[var(--n-accent)]' : 'text-[var(--n-ink)]'}`}>{formatVersionDate(v.created_at)}</span>
                  )}
                  <span className="truncate text-[11px] text-[var(--n-muted)]">{v.label ? formatVersionDate(v.created_at) : (v.snippet || 'Empty')}</span>
                </button>
              ))
            )}
          </div>
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center justify-between gap-2 border-b border-[var(--n-line)] px-4 py-3">
            {renaming ? (
              <div className="flex min-w-0 flex-1 items-center gap-1.5">
                <input autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') applyRename(); if (e.key === 'Escape') setRenaming(false); }}
                  placeholder="Version name" data-testid="note-history-rename-input"
                  className="n-focus min-w-0 flex-1 rounded-md border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1 text-[12px] text-[var(--n-ink)]" />
                <button onClick={applyRename} data-testid="note-history-rename-confirm" className="rounded-md bg-[var(--n-accent)] px-2 py-1 text-[11px] font-semibold text-white hover:opacity-90">Save</button>
                <button onClick={() => setRenaming(false)} className="rounded-md px-2 py-1 text-[11px] text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)]">Cancel</button>
              </div>
            ) : (
              <button onClick={() => { if (!selected) return; setRenameVal(selected.label || ''); setRenaming(true); }} disabled={!selected}
                data-testid="note-history-rename" title="Name this version"
                className="inline-flex min-w-0 items-center gap-1.5 text-[12px] text-[var(--n-muted)] hover:text-[var(--n-ink)] disabled:opacity-50">
                <Bookmark size={13} className="shrink-0 text-[var(--n-accent)]" />
                <span className="truncate">{selected ? (selected.label ? selected.label : 'Add a name…') : 'Preview'}</span>
                {selected && <span className="text-[var(--n-muted-2)]">· {formatVersionDate(selected.created_at)}</span>}
              </button>
            )}
            <div className="flex shrink-0 items-center gap-2">
              <div className="flex items-center rounded-lg border border-[var(--n-border)] p-0.5" data-testid="note-history-mode">
                <button onClick={() => setMode('preview')} data-testid="note-history-mode-preview"
                  className={`rounded-md px-2 py-1 text-[11px] font-medium ${mode === 'preview' ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>Preview</button>
                <button onClick={() => setMode('diff')} data-testid="note-history-mode-diff"
                  className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium ${mode === 'diff' ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}><GitCompare size={12} /> Changes</button>
              </div>
              <button onClick={restore} disabled={!selId || restoring} data-testid="note-history-restore"
                className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--n-accent)] px-3 py-1.5 text-[12px] font-semibold text-white hover:opacity-90 disabled:opacity-40">
                <RotateCcw size={13} /> {restoring ? 'Restoring…' : 'Restore'}
              </button>
              <button onClick={onClose} aria-label="Close" data-testid="note-history-close" className="text-[var(--n-muted-2)] hover:text-[var(--n-ink)]"><X size={16} /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto n-scroll px-6 py-5">
            {err && <div className="mb-2 text-[12px] text-[var(--n-danger)]" data-testid="note-history-error">{err}</div>}
            {selected ? (
              mode === 'diff' ? (
                <div data-testid="note-history-diff">
                  <div className="mb-3 flex flex-wrap items-center gap-3 text-[11px] text-[var(--n-muted)]">
                    <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-3.5 rounded-sm notes-diff-add" /> Added since this version</span>
                    <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-3.5 rounded-sm notes-diff-del" /> Removed since this version</span>
                  </div>
                  <div className="whitespace-pre-wrap text-[14px] leading-relaxed text-[var(--n-ink-soft)]">
                    {diff.some((p) => p.type !== 'same') ? (
                      diff.map((p, idx) => (
                        p.type === 'same'
                          ? <span key={idx}>{p.text}</span>
                          : <span key={idx} className={p.type === 'add' ? 'notes-diff-add' : 'notes-diff-del'}>{p.text}</span>
                      ))
                    ) : (
                      <span className="text-[var(--n-muted)]">No text changes between this version and the current note.</span>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <h3 className="font-display text-2xl font-bold text-[var(--n-ink)]">{selected.title || 'Untitled'}</h3>
                  <div className="ProseMirror mt-3" data-testid="note-history-preview" dangerouslySetInnerHTML={{ __html: selected.body_html || '<p style="opacity:.6">Empty note</p>' }} />
                </>
              )
            ) : (
              <div className="flex h-full items-center justify-center text-[13px] text-[var(--n-muted)]">Select a version to preview.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
